import moment from "moment";

// Libelles courts des cles "depuis" (since) ; repli sur le suffixe de la cle.
const LIBELLES_DEPUIS = [
  [/_DATEBASCULE\d*_DEBUT$/, "Début bascule"],
  [/_DATEBASCULE\d*_FIN$/, "Fin bascule"],
  [/_DATEBASCULE\d*$/, "Bascule"],
  [/_START$/, "Début"],
  [/_END$/, "Fin"],
];

export function libelleDepuis(cle) {
  const k = String(cle || "");
  const trouve = LIBELLES_DEPUIS.find(([re]) => re.test(k));
  return trouve ? trouve[1] : k;
}

export function formaterDateCourte(v) {
  if (!v) return "";
  const m = moment(String(v), [moment.ISO_8601, "DD/MM/YYYY"], true);
  return m.isValid() ? m.format("DD/MM/YYYY") : String(v);
}

export function formaterReleve(v) {
  if (!v) return null;
  const m = moment(String(v));
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : String(v);
}

// Statut -> { label, valeur, actif } ; ASPI/Sync (enabled/disabled), SIREN (valeur brute).
export function decrireStatut({ key, value }) {
  const k = String(key || "");
  let m = k.match(/PROVIDER(\d*)_(ASPI|SYNC)STATUS$/);
  if (m) {
    const rang = m[1] ? ` ${m[1]}` : "";
    return {
      label: `${m[2] === "ASPI" ? "ASPI" : "Sync"}${rang}`,
      valeur: String(value) === "enabled" ? "actif" : "inactif",
      actif: String(value) === "enabled",
      tag: true,
    };
  }
  m = k.match(/^RHSIREN(\d*)$/);
  if (m) return { label: `SIREN${m[1] ? ` ${m[1]}` : ""}`, valeur: String(value), actif: null, tag: false };
  return { label: k, valeur: String(value), actif: null, tag: false };
}

// Libelles de repli (groupe ou serveur supprime : nom vide cote API).
export const libelleClient = (c) =>
  c && String(c.name || "").trim() ? c.name : `Groupe inconnu (#${c && c.id})`;
export const libelleServeur = (s) =>
  s && String(s.hostname || "").trim() ? s.hostname : `Serveur inconnu (#${s && s.id})`;
const estInconnu = (c) => !String(c.name || "").trim();

// Clients nommes tries par nom, clients inconnus en fin de liste.
export function trierClients(clients) {
  return [...(Array.isArray(clients) ? clients : [])].sort(
    (a, b) =>
      Number(estInconnu(a)) - Number(estInconnu(b)) ||
      (estInconnu(a) ? a.id - b.id : String(a.name).localeCompare(String(b.name), "fr"))
  );
}

const liste = (v) => (Array.isArray(v) ? v : []);

// Statut unique d'un module (contrat v2) : libelle, couleur (vue d'ensemble, pastilles) et couleur de Tag antd.
export const STATUTS = {
  ok: { label: "Flux actif", couleur: "#2e9e6b", tag: "green" },
  observed_not_declared: { label: "Exécuté, non déclaré", couleur: "#ff8801", tag: "orange" },
  declared_not_observed: { label: "Déclaré, aucun flux sur 30 j", couleur: "#e5484d", tag: "red" },
  no_activity: { label: "Environnement sans activité (30 j)", couleur: "#b8c0cc", tag: "default" },
  missing: { label: "⚠ aucune source", couleur: "#e5484d", tag: "red" },
};
export const ORDRE_STATUTS = ["ok", "no_activity", "observed_not_declared", "declared_not_observed", "missing"];

export function statutModule(m) {
  if (m && STATUTS[m.status]) return m.status;
  return m && m.missing_source ? "missing" : "ok";
}
// Ecart = tout statut different de ok et no_activity.
export const estEcart = (m) => !["ok", "no_activity"].includes(statutModule(m));

// Compteurs d'un environnement : sans = modules missing (⚠), ecarts = autres ecarts.
export function compteursEnv(e) {
  let sans = 0;
  let ecarts = 0;
  liste(e && e.modules).forEach((m) => {
    const st = statutModule(m);
    if (st === "missing") sans += 1;
    else if (estEcart(m)) ecarts += 1;
  });
  return { sans, ecarts };
}

// Couleur d'un environnement : rouge (missing / declare non observe), orange (observe non declare), gris.
export function niveauEnv(e) {
  const sts = liste(e && e.modules).map(statutModule);
  if (sts.some((x) => x === "missing" || x === "declared_not_observed")) return "rouge";
  if (sts.includes("observed_not_declared")) return "orange";
  return "gris";
}

export function formaterDateHeure(v, format = "DD/MM/YYYY HH:mm") {
  if (!v) return null;
  const m = moment(String(v));
  return m.isValid() ? m.format(format) : String(v);
}

// Prestataires declares (actuelle puis anterieures) puis observes (30 j) d'un module.
export function prestatairesModule(mod) {
  const vals = [];
  if (mod.current_source && mod.current_source.value) vals.push(String(mod.current_source.value));
  liste(mod.previous_sources).forEach((s) => s && s.value && vals.push(String(s.value)));
  liste(mod.observed_sources).forEach((o) => o && o.provider && vals.push(String(o.provider)));
  return vals;
}

// Valeurs distinctes de prestataires de tout l'arbre (insensible a la casse, premiere graphie gardee).
export function prestatairesDistincts(clients) {
  const vus = new Map();
  liste(clients).forEach((c) =>
    liste(c.servers).forEach((s) =>
      liste(s.environments).forEach((e) =>
        liste(e.modules).forEach((m) =>
          prestatairesModule(m).forEach((p) => {
            if (!vus.has(p.toLowerCase())) vus.set(p.toLowerCase(), p);
          })
        )
      )
    )
  );
  return [...vus.values()].sort((a, b) => a.localeCompare(b, "fr"));
}

// Filtres combinables : clients (ids), serveurs (ids), prestataires, ecarts (statut different de ok et no_activity).
// Les parents sont conserves ; les noeuds vides sont elagues des qu'un filtre de module est actif.
export function filtrerArbre(clients, { clients: fc = [], serveurs = [], prestataires = [], ecarts = false } = {}) {
  const presta = prestataires.map((p) => String(p).toLowerCase());
  const filtreModule = ecarts || presta.length > 0;
  const moduleOk = (m) => {
    if (ecarts && !estEcart(m)) return false;
    if (presta.length && !prestatairesModule(m).some((p) => presta.includes(p.toLowerCase()))) return false;
    return true;
  };
  const resultat = [];
  liste(clients).forEach((c) => {
    if (fc.length && !fc.includes(c.id)) return;
    const servers = [];
    liste(c.servers).forEach((s) => {
      if (serveurs.length && !serveurs.includes(s.id)) return;
      const environments = [];
      liste(s.environments).forEach((e) => {
        const modules = liste(e.modules).filter(moduleOk);
        if (filtreModule && modules.length === 0) return;
        environments.push({ ...e, modules });
      });
      if (filtreModule && environments.length === 0) return;
      servers.push({ ...s, environments });
    });
    if ((filtreModule || serveurs.length) && servers.length === 0) return;
    resultat.push({ ...c, servers });
  });
  return resultat;
}

// Arbre -> lignes de Table (children) avec compteurs.
export function construireLignes(clients) {
  return liste(clients).map((c) => {
    let nbEnv = 0;
    let nbSans = 0;
    let nbEcarts = 0;
    const children = liste(c.servers).map((s) => {
      const envs = liste(s.environments);
      const cpt = envs.reduce(
        (a, e) => {
          const c = compteursEnv(e);
          return { sans: a.sans + c.sans, ecarts: a.ecarts + c.ecarts };
        },
        { sans: 0, ecarts: 0 }
      );
      nbEnv += envs.length;
      nbSans += cpt.sans;
      nbEcarts += cpt.ecarts;
      return {
        key: `s-${c.id}-${s.id}`,
        type: "server",
        nom: libelleServeur(s),
        detail: s.name,
        nbEnv: envs.length,
        nbSans: cpt.sans,
        nbEcarts: cpt.ecarts,
        children: envs.map((e) => ({
          key: `e-${c.id}-${s.id}-${e.id}`,
          type: "env",
          env: e,
          children: liste(e.modules).map((m) => ({
            key: `m-${e.id}-${m.name || m.code}`,
            type: "module",
            mod: m,
          })),
        })),
      };
    });
    return { key: `c-${c.id}`, type: "client", id: c.id, nom: libelleClient(c), nbEnv, nbSans, nbEcarts, children };
  });
}

// ---- Vue d'ensemble (icicle) : arbre hierarchique pret pour d3.hierarchy ----
// Feuille source : statut parmi STATUTS (ou "ancien" pour une source declaree anterieure).
export function construireGlacon(clients) {
  const noeud = (id, name, type, extra) => ({ id, name, type, ...extra });
  const racine = { id: "root", name: "Parc", type: "root", children: [] };
  liste(clients).forEach((c) => {
    const cn = noeud(`c${c.id}`, libelleClient(c), "client", { children: [] });
    liste(c.servers).forEach((s) => {
      const sn = noeud(`${cn.id}/s${s.id}`, libelleServeur(s), "server", { children: [] });
      liste(s.environments).forEach((e) => {
        const en = noeud(`${sn.id}/e${e.id}`, e.code, "env", {
          label: e.label,
          releve: e.settings_changed_at,
          hasSettings: !!e.has_settings,
          children: [],
        });
        liste(e.modules).forEach((m) => {
          const mn = noeud(`${en.id}/m${m.name || m.code}`, m.label || m.code || m.name, "module", {
            children: [],
            statutModule: statutModule(m),
          });
          const observes = liste(m.observed_sources).filter((o) => o && o.provider);
          const clesObserves = observes.map((o) => String(o.provider).toLowerCase());
          const actuelle = m.current_source && m.current_source.value ? String(m.current_source.value) : null;
          const feuilles = [];
          if (actuelle) {
            const vu = clesObserves.includes(actuelle.toLowerCase());
            feuilles.push({
              name: actuelle,
              source: actuelle,
              observe: vu,
              statut: vu ? "ok" : statutModule(m) === "no_activity" ? "no_activity" : "declared_not_observed",
            });
          }
          liste(m.previous_sources)
            .filter((x) => x && x.value)
            .forEach((x) =>
              feuilles.push({ name: String(x.value), source: String(x.value), anterieure: true, statut: "ancien" })
            );
          observes.forEach((o) => {
            if (actuelle && String(o.provider).toLowerCase() === actuelle.toLowerCase()) return;
            feuilles.push({
              name: String(o.provider),
              source: String(o.provider),
              observe: true,
              statut: "observed_not_declared",
            });
          });
          if (feuilles.length === 0) feuilles.push({ name: "Aucune source", statut: "missing" });
          feuilles.forEach((f, i) => mn.children.push(noeud(`${mn.id}/${i}`, f.name, "source", f)));
          en.children.push(mn);
        });
        if (en.children.length === 0) {
          delete en.children;
          en.statut = "no_activity";
        }
        sn.children.push(en);
      });
      if (sn.children.length === 0) {
        delete sn.children;
        sn.statut = "no_activity";
      }
      cn.children.push(sn);
    });
    if (cn.children.length === 0) {
      delete cn.children;
      cn.statut = "no_activity";
    }
    racine.children.push(cn);
  });
  if (racine.children.length === 0) delete racine.children;
  return racine;
}

// Statut agrege = le plus severe des statuts ; tout statut hors STATUTS (ancien...) est neutre.
export function pireStatutCarto(statuts) {
  const rang = (x) => {
    const i = ORDRE_STATUTS.indexOf(x);
    return i < 0 ? 0 : i; // ancien : neutre
  };
  return ORDRE_STATUTS[Math.max(0, ...statuts.map(rang))];
}

// Pagination controlee des tableaux : tailles 20/50/100/200, total en francais ("1-50 sur 78 jobs").
export const TAILLES_PAGE = ["20", "50", "100", "200"];
export function proprietesPagination({ page, total, unite, onChange }) {
  return {
    current: page.current,
    pageSize: page.pageSize,
    total,
    showSizeChanger: true,
    pageSizeOptions: TAILLES_PAGE,
    hideOnSinglePage: false,
    locale: { items_per_page: "/ page" },
    showTotal: (t, [debut, fin]) => `${debut}–${fin} sur ${t} ${unite}`,
    onChange: (current, pageSize) => onChange({ current, pageSize }),
  };
}
